import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../api/client';

interface Category { id: number; title: string; description: string; language: string; }
interface Doc { id: string; documentationName: string; description: string; language: string; documentDate: string; createdAt: string; }

export default function DocumentsPage() {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [categoryPage, setCategoryPage] = useState(0);
  const locale = i18n.language === 'en' ? 'en-US' : i18n.language === 'ky' ? 'ky-KG' : 'ru-RU';
  const categoriesPerPage = 7;

  useEffect(() => { axios.get(`${API_BASE}/categories/get-all?size=100`).then(r => setCategories(r.data)).catch(() => {}); }, [i18n.language]);

  useEffect(() => {
    setLoading(true);
    const url = selectedCategory ? `${API_BASE}/documentations/get-by-category/${selectedCategory}` : `${API_BASE}/documentations/get-all`;
    axios.get(url).then(r => setDocs(r.data)).catch(() => setDocs([])).finally(() => setLoading(false));
  }, [selectedCategory, i18n.language]);

  const handleDownload = (id: string, name: string) => {
    axios.get(`${API_BASE}/documentations/download/${id}`, { responseType: 'blob' })
      .then(r => {
        const contentType = r.headers['content-type'] || 'application/octet-stream';
        const disposition = r.headers['content-disposition'] || '';
        const match = disposition.match(/filename="?([^"]+)"?/);
        const filename = match ? match[1] : name;
        const blob = new Blob([r.data], { type: contentType as string });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        window.URL.revokeObjectURL(url);
      }).catch(() => {});
  };

  const totalPages = Math.ceil(categories.length / categoriesPerPage);
  const displayedCategories = categories.slice(categoryPage * categoriesPerPage, (categoryPage + 1) * categoriesPerPage);

  return (
    <div style={s.page}>
      <h1 style={s.pageTitle}>{t('docs_title')}</h1>

      {/* Категории */}
      <div style={s.categoriesSection}>
        <button style={{ ...s.catItem, ...(selectedCategory === null ? s.catActive : {}) }} onClick={() => setSelectedCategory(null)}>
          {t('docs_all')}
        </button>
        {displayedCategories.map(c => (
          <button key={c.id} style={{ ...s.catItem, ...(selectedCategory === c.id ? s.catActive : {}) }} onClick={() => setSelectedCategory(c.id)}>
            <div style={s.catTitle}>{c.title}</div>
            {c.description && <div style={s.catDesc}>{c.description}</div>}
          </button>
        ))}
      </div>

      {/* Пагинация категорий */}
      {totalPages > 1 && (
        <div style={s.pagination}>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              style={{ ...s.pageBtn, ...(categoryPage === i ? s.pageBtnActive : {}) }}
              onClick={() => setCategoryPage(i)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Документы */}
      {loading ? (
        <div style={s.empty}>{t('docs_loading')}</div>
      ) : docs.length === 0 ? (
        <div style={s.empty}>{t('docs_empty')}</div>
      ) : (
        <div style={s.list}>
          {docs.map(doc => (
            <div key={doc.id} style={s.docCard}>
              <div style={s.docIcon}>📄</div>
              <div style={s.docInfo}>
                <div style={s.docName}>{doc.documentationName}</div>
                {doc.description && <div style={s.docDesc}>{doc.description}</div>}
                <div style={s.docMeta}>
                  {doc.language && <span style={s.docTag}>{doc.language.toUpperCase()}</span>}
                  {doc.documentDate && <span style={s.docDate}>{new Date(doc.documentDate).toLocaleDateString(locale)}</span>}
                </div>
              </div>
              <button style={s.downloadBtn} onClick={() => handleDownload(doc.id, doc.documentationName)}>
                {t('docs_download')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 960, margin: '0 auto', padding: '48px 40px' },
  pageTitle: { fontSize: '2rem', color: '#1a3a6b', marginBottom: 28 },
  categoriesSection: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 },
  catItem: { padding: '16px 18px', borderRadius: 10, border: '2px solid #e0e6f0', background: '#fff', textAlign: 'left', cursor: 'pointer', transition: 'all 0.2s' },
  catActive: { borderColor: '#1a3a6b', background: '#f0f4fb', boxShadow: '0 2px 8px rgba(26,58,107,0.1)' },
  catTitle: { fontWeight: 600, color: '#1a2a4a', fontSize: '0.95rem', marginBottom: 4 },
  catDesc: { fontSize: '0.82rem', color: '#666', lineHeight: 1.4 },
  pagination: { display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 28 },
  pageBtn: { padding: '8px 14px', borderRadius: 8, border: '2px solid #e0e6f0', background: '#fff', color: '#555', cursor: 'pointer', fontWeight: 600 },
  pageBtnActive: { background: '#1a3a6b', color: '#fff', borderColor: '#1a3a6b' },
  empty: { textAlign: 'center', padding: 60, color: '#888', fontSize: '1rem' },
  list: { display: 'flex', flexDirection: 'column', gap: 14 },
  docCard: { display: 'flex', alignItems: 'center', gap: 18, background: '#fff', borderRadius: 10, padding: '18px 22px', boxShadow: '0 2px 10px rgba(0,0,0,0.07)' },
  docIcon: { fontSize: '2rem', flexShrink: 0 },
  docInfo: { flex: 1 },
  docName: { fontWeight: 700, color: '#1a2a4a', fontSize: '0.97rem', marginBottom: 4 },
  docDesc: { fontSize: '0.85rem', color: '#666', marginBottom: 6 },
  docMeta: { display: 'flex', gap: 10, alignItems: 'center' },
  docTag: { background: '#e8f0fe', color: '#1a3a6b', borderRadius: 4, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 700 },
  docDate: { fontSize: '0.82rem', color: '#999' },
  downloadBtn: { padding: '9px 18px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const, flexShrink: 0 },
};
